import React, { useState, useRef, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import SignatureCanvas from "react-signature-canvas";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { PenTool, Type, ArrowLeft, Check, RotateCcw } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;
const LOGO_URL = "https://customer-assets.emergentagent.com/job_pool-lesson-book/artifacts/c9s2nl0s_rr%20logo.png";

const buildContractText = (sessionLabel) => `IMPORTANT INFORMATION - ${sessionLabel.toUpperCase()} PRIVATE SWIM LESSONS

Drop-Off & Pick-Up:
Private ${sessionLabel} swim lessons are open to all swimmers, no camp enrollment required. A parent/guardian is responsible for getting your child to and from the pool on time for each lesson. Please have your child arrive ready to swim in their bathing suit. Pick-up is directly at the pool when the lesson ends. While you are welcome to watch your child's lesson, please do not interfere with the instructor during the session.

Cancellations, Make-Ups, Refund Policy:
If a family cancels for any reason (i.e. illness, vacation, etc.), our Swim Instructor will do their best to reschedule. However, a make-up lesson is not guaranteed. There are no refunds for missed lessons. If you know your child will miss a lesson, please notify us as soon as possible. Advance absences should be emailed to aquatics@rollingriver.com. Notify the Camp Office directly via phone (516-593-2267) or email (Camp@rollingriver.com) for day-of absences. If we cancel for any reason (i.e. weather, instructor availability, etc.), a make-up lesson will be scheduled or a refund for the missed lesson will be provided. Communication regarding canceled lessons will be through email, text, and/or phone. Your instructor will reach out to you directly to schedule a make-up lesson.

Agreement, Waiver, and Release:
I, the Parent, have carefully read the description of Lessons for which we are registering, and, in consideration for being permitted by the Instructor to participate in the activity, I hereby waive, release, and discharge any and all claims for damages for personal injury, death, or property damage which I may have, or which may hereafter inure to me, as a result of participation in Lessons. I hereby acknowledge that participation in Lessons does not guarantee that my child will be water-safe. This release is intended to discharge in advance the Instructor and Rolling River Day Camp from any and all liability arising out of, or connected in any way, with my participation in the Lessons, even though that liability may arise out of negligence or carelessness on the part of Instructor. It is understood that this Activity involves an element of risk and danger of accidents, and knowing those risks, I hereby assume those risks. It is further agreed that this waiver, release and assumption of risk is to be binding on my heirs and assigns. I agree to indemnify and to hold Instructors and Rolling River Day Camp free and harmless from any loss, liability, damage, cost, or expense which they may incur as the result of my or my child's injury or property damage that I may sustain while participating in the Activity.`;

const SESSION_LABELS = { june: "June (Pre Season)", july_august: "July-August (In Season)" };

export default function NonEnrolledContract() {
  const { registrationId } = useParams();
  const navigate = useNavigate();
  const sigCanvas = useRef(null);
  const [registration, setRegistration] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [signatureType, setSignatureType] = useState("canvas");
  const [typedSignature, setTypedSignature] = useState("");

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const res = await axios.get(`${API}/api/non-enrolled/registration/${registrationId}`);
        if (!mounted) return;
        setRegistration(res.data);
        if (res.data.status === "signed" || res.data.status === "confirmed") {
          navigate(`/non-enrolled/confirmation/${registrationId}`);
        }
      } catch {
        toast.error("Registration not found");
        navigate("/");
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, [registrationId, navigate]);

  const clearSignature = () => {
    if (sigCanvas.current) sigCanvas.current.clear();
  };

  const handleSubmit = async () => {
    let signature = "";
    if (signatureType === "canvas") {
      if (sigCanvas.current?.isEmpty()) {
        toast.error("Please sign the contract");
        return;
      }
      signature = sigCanvas.current.toDataURL();
    } else {
      if (!typedSignature.trim()) {
        toast.error("Please type your signature");
        return;
      }
      signature = typedSignature.trim();
    }

    setSubmitting(true);
    try {
      await axios.post(`${API}/api/non-enrolled/sign-contract`, {
        registration_id: registrationId,
        signature,
        signature_type: signatureType,
      });
      toast.success("Contract signed successfully!");
      navigate(`/non-enrolled/confirmation/${registrationId}`);
    } catch {
      toast.error("Failed to sign contract. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FDFBF7]">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-[#D4A843] border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-slate-600">Loading contract...</p>
        </div>
      </div>
    );
  }

  if (!registration) return null;
  const sessionLabel = SESSION_LABELS[registration.session] || "June";

  const dates = registration.confirmed_dates || registration.choice1_dates || [];
  const dayLabel = registration.confirmed_day?.charAt(0).toUpperCase() + registration.confirmed_day?.slice(1);
  const price = registration.confirmed_price ?? registration.total_price ?? 0;
  const lessonCount = registration.lesson_count ?? dates.length;
  const perLesson = registration.price_per_lesson ?? (lessonCount ? (price / lessonCount) : 0);

  return (
    <div className="min-h-screen bg-white">
      <header className="bg-white shadow-sm border-b border-slate-100">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <img src={LOGO_URL} alt="Rolling River Day Camp" className="h-14" />
          <Button variant="ghost" onClick={() => navigate("/")} className="text-slate-600" data-testid="ne-contract-back">
            <ArrowLeft className="w-4 h-4 mr-2" /> Back
          </Button>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-8">
        <div className="card animate-fade-in">
          <h1 className="font-heading text-3xl font-bold text-[#D4A843] mb-2">
            {sessionLabel} Private Swim Lesson Contract
          </h1>
          <p className="text-slate-600 mb-6">
            Please read the contract carefully and sign below.
          </p>

          {/* Registration Summary */}
          <div className="mb-6">
            <h2 className="font-heading text-xl font-bold text-slate-800 mb-4">Your Lesson Details</h2>

            <div className="bg-slate-50 rounded-xl p-4 mb-4">
              <p className="text-lg"><strong>Child:</strong> {registration.child_first_name} {registration.child_last_name}</p>
              <p><strong>Parent:</strong> {registration.parent_first_name} {registration.parent_last_name}</p>
            </div>

            <div className="bg-[#FDF6E3] border-2 border-[#D4A843] rounded-xl p-4 mb-4">
              <div className="grid grid-cols-2 gap-2 mb-3">
                <p className="text-lg"><strong>Day:</strong> {dayLabel}s</p>
                <p className="text-lg"><strong>Time:</strong> {registration.confirmed_time || registration.choice1_time}</p>
                <p><strong>Lessons:</strong> {lessonCount} lesson{lessonCount !== 1 ? "s" : ""}</p>
                <p><strong>Total:</strong> ${Number(price).toFixed(2)} <span className="text-slate-500 text-sm">(${Number(perLesson).toFixed(2)}/lesson)</span></p>
                <p className="col-span-2"><strong>Lifeguard:</strong> {registration.assigned_instructor || registration.choice1_lifeguard_name}</p>
              </div>
              <div className="bg-white rounded-lg p-3">
                <p className="font-semibold text-[#92400E] text-sm mb-2">Your Lesson Dates:</p>
                <div className="flex flex-wrap gap-2" data-testid="ne-contract-dates">
                  {dates.map((date, idx) => (
                    <span key={idx} className="px-3 py-1 bg-[#FDF6E3] border border-[#D4A843] rounded text-sm font-medium">
                      {date}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Contract Text */}
          <div className="bg-slate-50 rounded-xl p-6 mb-6 max-h-96 overflow-y-auto">
            <pre className="whitespace-pre-wrap font-body text-sm text-slate-700 leading-relaxed">
              {buildContractText(sessionLabel)}
            </pre>
          </div>

          {/* Signature Section */}
          <div className="border-t border-slate-200 pt-6">
            <h2 className="font-heading text-xl font-bold text-slate-800 mb-4">Sign Below</h2>

            <Tabs value={signatureType} onValueChange={setSignatureType} className="w-full">
              <TabsList className="grid w-full grid-cols-2 mb-4">
                <TabsTrigger value="canvas" className="flex items-center gap-2">
                  <PenTool className="w-4 h-4" /> Draw Signature
                </TabsTrigger>
                <TabsTrigger value="typed" className="flex items-center gap-2">
                  <Type className="w-4 h-4" /> Type Signature
                </TabsTrigger>
              </TabsList>

              <TabsContent value="canvas">
                <div className="relative">
                  <SignatureCanvas
                    ref={sigCanvas}
                    canvasProps={{
                      className: "signature-pad w-full",
                      "data-testid": "ne-signature-canvas"
                    }}
                    backgroundColor="#f8fafc"
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={clearSignature}
                    className="absolute top-2 right-2"
                    data-testid="ne-clear-signature-btn"
                  >
                    <RotateCcw className="w-4 h-4 mr-1" /> Clear
                  </Button>
                </div>
                <p className="text-xs text-slate-500 mt-2 text-center">
                  Draw your signature above using your mouse or touch screen
                </p>
              </TabsContent>

              <TabsContent value="typed">
                <div>
                  <Label className="text-sm font-bold text-slate-700 uppercase tracking-wider mb-2 block">
                    Type Your Full Name as Signature
                  </Label>
                  <Input
                    data-testid="ne-typed-signature-input"
                    className="form-input w-full text-2xl font-signature"
                    style={{ fontFamily: "'Dancing Script', cursive, serif" }}
                    placeholder="Your Full Name"
                    value={typedSignature}
                    onChange={(e) => setTypedSignature(e.target.value)}
                  />
                </div>
              </TabsContent>
            </Tabs>

            <div className="mt-6 p-4 bg-yellow-50 rounded-xl border border-yellow-200">
              <p className="text-sm text-yellow-800">
                <strong>Parent/Guardian:</strong> {registration.parent_first_name} {registration.parent_last_name}
              </p>
              <p className="text-sm text-yellow-800 mt-1">
                By signing, I agree to the terms and conditions stated in this contract.
              </p>
            </div>

            <Button
              onClick={handleSubmit}
              disabled={submitting}
              className="w-full mt-6 bg-[#D4A843] hover:bg-[#b8902e] text-white py-6 text-lg"
              data-testid="ne-sign-contract-btn"
            >
              {submitting ? (
                "Signing..."
              ) : (
                <>
                  <Check className="w-5 h-5 mr-2" /> Sign Contract
                </>
              )}
            </Button>
          </div>
        </div>
      </main>
    </div>
  );
}
